import React from 'react';
import { windowExists } from '../common/utils/AccountUtils';
import { calculateStatus } from '../components/Sweepstakes/utils/SweepstakesDataMapper';
import { analyticsVideo } from '../common/utils/AnalyticsUtils';
import { VIDEO_ANALYTICS } from '../core/Constants';

const VIDEO_PLAYER_START_EVENT = 'video-player.start';
const VIDEO_PLAYER_COMPLETE_EVENT = 'video-player.complete';

export const useStepVideoCompletion = ({
  step,
  videoConfig,
  sweepstakesData,
  updateSweepstakesData,
  completeSweepstakesVideoStep,
  isLoadingCompleteVideo,
}) => {
  const hasHandledVideoStartRef = React.useRef(false);
  const [videoStarted, setVideoStarted] = React.useState(false);
  const stepId = step?.id;
  const customerChallengeStepId = step?.customerChallengeStepId;
  const videoId = videoConfig?.videoId;
  const isVideoStep = Boolean(videoConfig);
  const isStepCompleted = Boolean(step?.completed);

  // Keep mutable values in refs so handleVideoPlayStart doesn't need them in its dep array.
  // This prevents the subscription from being torn down and re-established on every render.
  const sweepstakesDataRef = React.useRef(sweepstakesData);
  const updateSweepstakesDataRef = React.useRef(updateSweepstakesData);
  const completeSweepstakesVideoStepRef = React.useRef(completeSweepstakesVideoStep);
  const isLoadingCompleteVideoRef = React.useRef(isLoadingCompleteVideo);
  sweepstakesDataRef.current = sweepstakesData;
  updateSweepstakesDataRef.current = updateSweepstakesData;
  completeSweepstakesVideoStepRef.current = completeSweepstakesVideoStep;
  isLoadingCompleteVideoRef.current = isLoadingCompleteVideo;

  React.useEffect(() => {
    hasHandledVideoStartRef.current = false;
    setVideoStarted(false);
  }, [stepId]);

  const handleVideoPlayStart = React.useCallback(async () => {
    if (!isVideoStep || isStepCompleted || isLoadingCompleteVideoRef.current || hasHandledVideoStartRef.current) return;

    hasHandledVideoStartRef.current = true;
    setVideoStarted(true);

    const challengeId = sweepstakesDataRef.current?.challengeId;
    const customerChallengeId = sweepstakesDataRef.current?.customerChallengeId;
    const currentStepData = sweepstakesDataRef.current?.steps?.find((candidate) => candidate.id === stepId);
    const resolvedChallengeStepId = currentStepData?.customerChallengeStepId || customerChallengeStepId;
    if (!customerChallengeId || !resolvedChallengeStepId) {
      hasHandledVideoStartRef.current = false;
      return;
    }

    const result = await completeSweepstakesVideoStepRef.current?.({
      challengeId,
      customerChallengeId,
      customerChallengeStepId: resolvedChallengeStepId,
    });

    if (!result?.success || !updateSweepstakesDataRef.current) {
      hasHandledVideoStartRef.current = false;
      setVideoStarted(false);
      return;
    }

    const currentCompletedSteps = sweepstakesDataRef.current?.completedSteps || 0;
    const totalSteps = sweepstakesDataRef.current?.totalSteps || 0;
    const nextCompletedSteps = Math.min(currentCompletedSteps + 1, totalSteps || currentCompletedSteps + 1);
    const nextStatus = calculateStatus(nextCompletedSteps, totalSteps, sweepstakesDataRef.current?.isOptedIn);

    updateSweepstakesDataRef.current({
      completedSteps: nextCompletedSteps,
      status: nextStatus,
      steps: [{ id: stepId, completed: true }],
    });
  }, [isStepCompleted, isVideoStep, stepId, videoId]);

  React.useEffect(() => {
    if (!isVideoStep || !windowExists()) return undefined;

    const eventBus = window.LIFE_CYCLE_EVENT_BUS;
    if (!eventBus) return undefined;

    const handleVideoStartEvent = (eventPayload = {}) => {
      const payload = eventPayload?.output || eventPayload;
      const payloadVideoId = payload?.videoId || payload?.id || payload?.video_id;

      // The event bus is page-global, so other Brightcove players on the host page can fire
      // the same events. Ignore events whose id doesn't match ours; allow through when either id is absent.
      if (videoId && payloadVideoId && String(payloadVideoId) !== String(videoId)) return;

      analyticsVideo(VIDEO_ANALYTICS.VIDEO_START, { id: videoId || payloadVideoId });
      if (!isStepCompleted) {
        handleVideoPlayStart();
      }
    };

    const handleVideoCompleteEvent = (eventPayload = {}) => {
      const payload = eventPayload?.output || eventPayload;
      const payloadVideoId = payload?.videoId || payload?.id || payload?.video_id;

      // The event bus is page-global, so other Brightcove players on the host page can fire
      // the same events. Ignore events whose id doesn't match ours; allow through when either id is absent.
      if (videoId && payloadVideoId && String(payloadVideoId) !== String(videoId)) return;

      analyticsVideo(VIDEO_ANALYTICS.VIDEO_COMPLETE, { id: videoId || payloadVideoId });
    };

    if (eventBus?.on) {
      eventBus.on(VIDEO_PLAYER_START_EVENT, handleVideoStartEvent);
      eventBus.on(VIDEO_PLAYER_COMPLETE_EVENT, handleVideoCompleteEvent);
    }

    return () => {
      if (eventBus?.off) {
        eventBus.off(VIDEO_PLAYER_START_EVENT, handleVideoStartEvent);
        eventBus.off(VIDEO_PLAYER_COMPLETE_EVENT, handleVideoCompleteEvent);
      }
    };
  }, [handleVideoPlayStart, isStepCompleted, isVideoStep, stepId, videoId]);

  return { videoStarted };
};
